import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { useFadeUp } from "@/hooks/useFadeUp";
import type { LandingContent, Faq } from "./types";
import { CALENDLY } from "./types";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/ai-automation-agency-houston/", label: "Houston" },
  { to: "/ai-readiness-assessment/", label: "Free Assessment" },
  { to: "/industries/ai-agents-for-law-firms/", label: "Law Firms" },
  { to: "/industries/ai-automation-for-logistics/", label: "Logistics" },
];

function LandingNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background-50/85 backdrop-blur-xl border-b border-background-200/70" : "bg-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-10 lg:px-16 py-4">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center cursor-pointer" aria-label="AIdeal Media home">
            <img
              src="/img/logo-imagotipo-positivo.webp"
              alt="AIdeal Media"
              className="h-[68px] w-auto object-contain"
            />
          </Link>
          <ul className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors"
                  style={scrolled ? { color: "#215E88" } : undefined}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="hidden lg:block">
            <a
              href={CALENDLY}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap"
            >
              Book Free Assessment
            </a>
          </div>
          <button
            className="lg:hidden w-10 h-10 flex items-center justify-center text-background-50"
            style={scrolled ? { color: "#215E88" } : undefined}
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            <i className={`ri-${open ? "close" : "menu"}-line text-2xl`}></i>
          </button>
        </div>
        {open && (
          <ul className="lg:hidden mt-2 space-y-1 bg-background-50 rounded-lg p-4 shadow-xl">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2.5 rounded-md text-sm font-medium"
                  style={{ color: "#215E88" }}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </nav>
    </header>
  );
}

function FaqAccordion({ faqs }: { faqs: Faq[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="mt-14 space-y-3">
      {faqs.map((f, i) => {
        const isOpen = open === i;
        return (
          <div
            key={i}
            className={`glass-card rounded-lg border ${
              isOpen ? "border-[#02B36D]/40" : "border-background-200/70"
            } transition-colors`}
          >
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              className="w-full flex items-start justify-between gap-4 px-5 md:px-6 py-5 text-left cursor-pointer"
            >
              <span className="font-heading text-base md:text-lg font-bold pt-[6px]" style={{ color: "#215E88" }}>
                {f.q}
              </span>
              <span
                className={`w-9 h-9 min-w-[36px] min-h-[36px] shrink-0 flex items-center justify-center rounded-full transition-all ${
                  isOpen ? "bg-[#02B36D] text-background-50 rotate-45" : "bg-background-100 text-[#215E88]"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-add-line text-lg"></i>
                </span>
              </span>
            </button>
            {isOpen && (
              <div className="px-5 md:px-6 pb-5 text-sm md:text-base leading-relaxed animate-fadeIn" style={{ color: "#215E88" }}>
                {f.a}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function SectionHead({ eyebrow, title, intro, dark }: { eyebrow: string; title: React.ReactNode; intro?: string; dark?: boolean }) {
  return (
    <div className="text-left max-w-2xl">
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">
        <span className="w-2 h-2 rounded-full bg-primary-600"></span>
        {eyebrow}
      </span>
      <h2 className={`mt-5 font-heading text-3xl md:text-5xl font-bold leading-tight ${dark ? "text-background-50" : ""}`}>
        {title}
      </h2>
      {intro && <p className={`mt-4 text-base md:text-lg leading-relaxed ${dark ? "text-background-200/85" : "text-foreground-600"}`}>{intro}</p>}
    </div>
  );
}

export default function LandingPage({ content: c }: { content: LandingContent }) {
  // Self-referencing hreflang: these pages are English-only (no verified
  // Spanish equivalents), so en + x-default point at the canonical URL.
  useSeo({
    ...c.seo,
    alternates: [
      { hreflang: "en", href: c.seo.canonical },
      { hreflang: "x-default", href: c.seo.canonical },
    ],
  });
  const { ref, visible } = useFadeUp();

  return (
    <main className="relative bg-background-50 text-foreground-900 font-body">
      <LandingNavbar />

      {/* HERO */}
      <section className="relative w-full min-h-[720px] md:min-h-[800px] flex items-center justify-center overflow-hidden pt-28 pb-24">
        <div className="absolute inset-0">
          <img src="/img/hero-bg.jpg" alt="AI network background" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/85 via-primary-900/75 to-primary-950/90"></div>
          <div className="absolute inset-0 grid-pattern opacity-20"></div>
        </div>
        <div className="absolute top-24 left-[8%] w-72 h-72 rounded-full radial-glow opacity-40 animate-floaty"></div>
        <div className="absolute bottom-16 right-[6%] w-96 h-96 rounded-full radial-glow opacity-30 animate-floaty" style={{ animationDelay: "1.5s" }}></div>

        <div className="relative z-10 w-full max-w-7xl px-6 md:px-10">
          <div className="flex flex-col items-center text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-dark text-background-50/90 text-xs md:text-sm font-medium mb-8 animate-fadeUp">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-sparkling-2-fill text-accent-400"></i>
              </span>
              {c.badge}
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulseGlow"></span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-background-50 leading-[1.05] max-w-5xl animate-fadeUp" style={{ animationDelay: "0.1s" }}>
              {c.h1a}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(to right, #215E88, #02B36D)" }}>
                {c.h1gradient}
              </span>
              {c.h1b}
            </h1>
            <p className="mt-6 text-base md:text-lg text-background-200/90 max-w-2xl leading-relaxed animate-fadeUp" style={{ animationDelay: "0.2s" }}>
              {c.sub}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-4 animate-fadeUp" style={{ animationDelay: "0.3s" }}>
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="group px-7 py-3.5 rounded-md text-sm md:text-base font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                Get My Free Assessment
                <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                  <i className="ri-arrow-right-line"></i>
                </span>
              </a>
              <Link
                to="/ai-readiness-assessment/"
                className="px-7 py-3.5 rounded-md text-sm md:text-base font-semibold text-background-50 border border-background-50/30 hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                How the assessment works
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <SectionHead
            eyebrow="The problem"
            title={<><span style={{ color: "#215E88" }}>{c.problemTitle}</span></>}
            intro={c.problemIntro}
          />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {c.pains.map((p, i) => (
              <div key={i} className="glass-card rounded-2xl border border-background-200/70 p-8">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl text-background-50" style={{ background: "linear-gradient(135deg,#215E88,#02B36D)" }}>
                  <i className={p.icon}></i>
                </div>
                <h3 className="mt-5 font-heading text-xl font-bold" style={{ color: "#215E88" }}>{p.title}</h3>
                <p className="mt-3 text-foreground-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES / USE CASES */}
      <section className="relative py-24 md:py-32 bg-background-100/60 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <SectionHead eyebrow="What we deploy" title={<><span style={{ color: "#215E88" }}>{c.servicesTitle}</span></>} intro={c.servicesIntro} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.services.map((s, i) => (
              <div key={i} className="glass-card rounded-2xl border border-background-200/70 p-8 hover:border-[#02B36D]/40 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                  <i className={s.icon}></i>
                </div>
                <h3 className="mt-5 font-heading text-xl font-bold" style={{ color: "#215E88" }}>{s.title}</h3>
                <p className="mt-3 text-foreground-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HIGHLIGHT BAND */}
      <section className="relative py-24 md:py-28 overflow-hidden" style={{ background: "linear-gradient(135deg,#0D2537,#123049)" }}>
        <div className="absolute inset-0 grid-pattern opacity-15"></div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-background-50">{c.bandTitle}</h2>
          <p className="mt-4 text-background-200/85 text-base md:text-lg max-w-3xl leading-relaxed">{c.bandText}</p>
          <ul className="mt-8 space-y-4">
            {c.bandPoints.map((pt, i) => (
              <li key={i} className="flex items-start gap-3 text-background-50/90 text-base md:text-lg">
                <span className="mt-1 w-6 h-6 shrink-0 rounded-full bg-[#02B36D]/20 border border-[#02B36D]/40 flex items-center justify-center">
                  <i className="ri-check-line text-[#02B36D]"></i>
                </span>
                <span>{pt}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <SectionHead eyebrow="How we work" title={<><span style={{ color: "#215E88" }}>{c.processTitle}</span></>} intro={c.processIntro} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.steps.map((s, i) => (
              <div key={i} className="relative glass-card rounded-2xl border border-background-200/70 p-8">
                <span className="font-heading text-5xl font-bold text-primary-100">0{i + 1}</span>
                <h3 className="mt-4 font-heading text-xl font-bold" style={{ color: "#215E88" }}>{s.title}</h3>
                <p className="mt-3 text-foreground-600 leading-relaxed text-sm md:text-base">{s.text}</p>
                <span className="mt-4 inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold">{s.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-16 md:py-20 bg-background-100/60">
        <div ref={ref} className={`max-w-7xl mx-auto px-6 md:px-10 fade-up-hidden ${visible ? "fade-up-visible" : ""}`}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {c.stats.map((st, i) => (
              <div key={i}>
                <div className="font-heading text-4xl md:text-5xl font-bold brand-gradient-text">{st.value}</div>
                <div className="mt-2 text-sm md:text-base text-foreground-600">{st.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <SectionHead eyebrow="FAQ" title={<><span style={{ color: "#215E88" }}>{c.faqTitle}</span></>} />
          <FaqAccordion faqs={c.faqs} />
        </div>
      </section>

      {/* RELATED / INTERNAL LINKS */}
      <section className="relative py-16 md:py-20 bg-background-100/60">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold" style={{ color: "#215E88" }}>{c.relatedTitle}</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            {c.related.map((r, i) => (
              <Link key={i} to={r.href} className="group glass-card rounded-2xl border border-background-200/70 p-6 hover:border-[#02B36D]/40 transition-colors">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-bold" style={{ color: "#215E88" }}>{r.label}</h3>
                  <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center transition-transform group-hover:translate-x-1">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
                <p className="mt-2 text-sm text-foreground-600">{r.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-28 bg-background-50">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-800 via-primary-900 to-primary-950 p-10 md:p-16">
            <div className="absolute -top-24 -right-24 w-[400px] h-[400px] radial-glow opacity-50"></div>
            <div className="absolute inset-0 grid-pattern opacity-15"></div>
            <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
              <div className="max-w-2xl">
                <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">{c.ctaTitle}</h2>
                <p className="mt-4 text-background-200/85 text-base md:text-lg max-w-xl">{c.ctaText}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch gap-3 w-full lg:w-auto">
                <a
                  href={CALENDLY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group px-6 py-3.5 rounded-md text-sm font-semibold text-background-50 brand-gradient-btn cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  Book My Free Session
                  <span className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="relative bg-primary-950 text-background-200/80 py-14">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <Link to="/" className="flex items-center">
              <img src="/img/logo-imagotipo-positivo.webp" alt="AIdeal Media" className="h-14 w-auto object-contain" />
            </Link>
            <ul className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-background-50 transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-10 pt-8 border-t border-background-50/10 flex flex-col md:flex-row justify-between gap-4 text-xs text-background-200/60">
            <span>© {new Date().getFullYear()} AIdeal Media. All rights reserved.</span>
            <span className="flex gap-6">
              <Link to="/privacidad" className="hover:text-background-50">Privacy</Link>
              <Link to="/terminos" className="hover:text-background-50">Terms</Link>
              <Link to="/cookies" className="hover:text-background-50">Cookies</Link>
            </span>
          </div>
        </div>
      </footer>
    </main>
  );
}
